import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import CategoryCard from "../CategoryCard";
import SinglePropSelectModal from "../SinglePropSelectModal";
import MultiPropSelectModal from "../MultiPropSelectModal";
import { BadgeGroup, ImpactText, ModalHeader, PrimaryAction, RecommendedBadge, ReplicateContent, ReplicateHeader, ReplicateSection, ReplicateSelectionCard, ReplicateSubtitle, ReplicateTitle } from "../Styles/InputStyles";

const EvictionSettingsModal = ({
  onClose,
  settingsData,
  editSettingsMutate,
  isLoading,
  property,
  propertyList,
  fetchSettingsLoading,
  isDataLoaded,
  propertyParam,
  fetchEvictionSettingsMutate,
  editSettingsLoading,
  selectedProperties,
  setSelectedProperties
}) => {
  const [settings, setSettings] = useState({
    autoEvictTenant: false,
    keyHandoverOTP: false,
    lockinPeriodServe: false,
    stopRequestIfUnpaid: false,
    extraMonthRentMidCycle: false,
    restrictMoveOutWindow: false,

  });

  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState();
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [isPropertyChanging, setIsPropertyChanging] = useState(false);
  const allProperties = propertyList || [];


  useEffect(() => {
    if (propertyParam && filteredProperties.length > 0) {
      const propertyFromParam = filteredProperties.find(prop => prop.id === propertyParam);
      if (propertyFromParam && propertyFromParam.id !== selectedPropertyId) {
        setSelectedProperty(propertyFromParam);
        setSelectedPropertyId(propertyFromParam.id);
      }
    }
  }, [propertyParam, filteredProperties]);

  useEffect(() => {
    if (propertyList) {
      const filtered = propertyList?.filter(prop => prop.id !== 0) || [];
      setFilteredProperties(filtered);

      if (filtered.length > 0 && !selectedProperty) {
        let initialProperty;

        if (propertyParam && propertyParam !== 0) {
          initialProperty = filtered.find(prop => prop.id === propertyParam);
        }

        if (!initialProperty) {
          initialProperty = filtered[0];
        }

        setSelectedProperty(initialProperty);
        setSelectedPropertyId(initialProperty.id);

        if (fetchEvictionSettingsMutate && initialProperty.id) {
          fetchEvictionSettingsMutate(initialProperty.id);
        }
      }
    }
  }, [propertyList, propertyParam, fetchEvictionSettingsMutate, selectedProperty]);

  useEffect(() => {
    if (settingsData) {
      setSettings({
        lockinPeriodServe: settingsData?.lockInServe === 1,
        stopRequestIfUnpaid: settingsData?.clearDues === 1,
        extraMonthRentMidCycle: settingsData?.midCycleCharge === 1,
        restrictMoveOutWindow: settingsData?.restrictMoveOut === 1,
      });
    }
  }, [settingsData]);

  const toggleSetting = (key) => {
    const newValue = !settings[key];

    const updatedSettings = {
      ...settings,
      [key]: newValue
    };

    setSettings(updatedSettings);

    const payload = {
      propIds: [selectedPropertyId],
      lockInServe: updatedSettings.lockinPeriodServe ? 1 : 0,
      clearDues: updatedSettings.stopRequestIfUnpaid ? 1 : 0,
      midCycleCharge: updatedSettings.extraMonthRentMidCycle ? 1 : 0,
      restrictMoveOut: updatedSettings.restrictMoveOutWindow ? 1 : 0,
      replicate: 0,
      selectedPropertyId: selectedPropertyId
    };
    editSettingsMutate(payload);
  };

  const handleSelectedProperty = (selectedOption) => {
    const newPropertyId = selectedOption.id;

    if (newPropertyId === selectedPropertyId) {
      setSelectedProperty(selectedOption);
      return;
    }

    setIsPropertyChanging(true);
    setSelectedProperty(selectedOption);
    setSelectedPropertyId(newPropertyId);

    setSettings({
      lockinPeriodServe: false,
      stopRequestIfUnpaid: false,
      extraMonthRentMidCycle: false,
      restrictMoveOutWindow: false,
    });

    if (fetchEvictionSettingsMutate) {
      fetchEvictionSettingsMutate(newPropertyId, {
        onSettled: () => {
          setIsPropertyChanging(false);
        }
      });
    } else {
      setIsPropertyChanging(false);
    }
  };

  const handleReplicateSettings = () => {
    if (selectedProperties.length === 0) {
      setShowMultiSelectModal(true);
      return;
    }

    const payload = {
      propIds: selectedProperties,
      lockInServe: settings.lockinPeriodServe ? 1 : 0,
      clearDues: settings.stopRequestIfUnpaid ? 1 : 0,
      midCycleCharge: settings.extraMonthRentMidCycle ? 1 : 0,
      restrictMoveOut: settings.restrictMoveOutWindow ? 1 : 0,
      replicate: 1,
      selectedPropertyId: selectedPropertyId
    };

    editSettingsMutate(payload);
  };

  const handleMultiPropertySave = (selectedProps) => {
    setSelectedProperties(selectedProps);
    setShowMultiSelectModal(false);
  };

  const renderSelectedProperties = (selected = []) => {
    if (!selected.length) return "No properties selected";

    if (selected.length === allProperties.length) {
      return "All Properties";
    }

    const selectedPropertyNames = allProperties
      .filter((prop) => selected.includes(prop.id))
      .map((prop) => prop.name);

    if (!selectedPropertyNames.length) return "No properties selected";

    const maxToShow = 2;
    const namesToShow = selectedPropertyNames.slice(0, maxToShow);
    const remainingCount = selectedPropertyNames.length - maxToShow;

    return (
      <PropertyDisplayContainer>
        {namesToShow.join(", ")}
        {remainingCount > 0 && (
          <RemainingCount>+{remainingCount}</RemainingCount>
        )}
      </PropertyDisplayContainer>
    );
  };

  const showLoading = fetchSettingsLoading || !isDataLoaded || isPropertyChanging;

  return (
    <div className="bg-white w-full h-full flex flex-col">
      <ModalHeader>
        <HeaderTitle>Eviction Settings</HeaderTitle>
        <CloseButton onClick={onClose}>
          <MdClose color="white" size={24} />
        </CloseButton>
      </ModalHeader>
      {showLoading ? (
        <div className="flex items-center justify-center h-full">
          <CircularProgress />
        </div>
      ) : (
        <>
          <ContentContainer>
            <ContentWrapper>
              <Section>
                {property?.id === 0 && filteredProperties.length > 0 && (
                  // <PropertySection>
                  <CategoryCard
                    title="Property"
                    selectedCategory={selectedProperty}
                    onClick={() => setShowPropertyModal(true)}
                  />
                  // </PropertySection>
                )}

                <SettingsGrid>
                  {/* <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Lock-in Period Serve</SettingTitle>
                        <SettingDescription>
                          Enforce mandatory lock-in period completion before allowing tenant eviction or early termination requests.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.lockinPeriodServe}
                        onChange={() => toggleSetting("lockinPeriodServe")}
                        disabled={isLoading || isPropertyChanging}
                      />
                    </SettingContent>
                  </SettingCard>
                  <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Pending Dues</SettingTitle>
                        <SettingDescription>
                          Tenant must clear all pending dues before submitting eviction or early termination requests.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.stopRequestIfUnpaid}
                        onChange={() => toggleSetting("stopRequestIfUnpaid")}
                        disabled={isLoading || isPropertyChanging}
                      />
                    </SettingContent>
                  </SettingCard>
                  <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Restrict Move-Out Window</SettingTitle>
                        <SettingDescription>
                          Allow tenants to submit move-out requests only on the rent cycle date.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.restrictMoveOutWindow}
                        onChange={() => toggleSetting("restrictMoveOutWindow")}
                        disabled={isLoading || isPropertyChanging}
                      />
                    </SettingContent>
                  </SettingCard>
                  <SettingCard>
                    <SettingContent>
                      <SettingHeader>
                        <SettingTitle>Extra Month Rent for Mid-Cycle Move-Out</SettingTitle>
                        <SettingDescription>
                          Require tenants to pay an additional month's rent if they request move-out in the middle of a rental cycle.
                        </SettingDescription>
                      </SettingHeader>
                      <Toggle
                        checked={settings.extraMonthRentMidCycle}
                        onChange={() => toggleSetting("extraMonthRentMidCycle")}
                        disabled={isLoading || isPropertyChanging}
                      />
                    </SettingContent>
                  </SettingCard> */}
                  {/* SECTION HEADER */}
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Move-Out Rules
                    </p>
                  </div>

                  {/* Enforce Lock-in */}
                  <SettingCard $variant="info" $active={settings.lockinPeriodServe}>
                    <SettingContent>

                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>Enforce Lock-in Period</SettingTitle>

                          <BadgeGroup>
                            {!settings.lockinPeriodServe && (
                              <RecommendedBadge>Recommended</RecommendedBadge>
                            )}

                            {settings.lockinPeriodServe && (
                              <StatusBadge $variant="info">Active</StatusBadge>
                            )}
                          </BadgeGroup>
                        </TitleRow>

                        <SettingDescription>
                          Tenants cannot request move-out before completing the minimum stay period.
                        </SettingDescription>

                        <ImpactText className="text-green-600">
                          ↑ Reduces early move-outs and improves revenue stability
                        </ImpactText>

                        {!settings.lockinPeriodServe && (
                          <HelperText>
                            If not set, Tenants will be allowed to leave anytime.
                          </HelperText>
                        )}
                      </SettingHeader>

                      <ToggleWrapper>
                        <Toggle
                          checked={settings.lockinPeriodServe}
                          onChange={() => toggleSetting("lockinPeriodServe")}
                          disabled={isLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>

                    </SettingContent>
                  </SettingCard>

                  {/* Block if unpaid */}
                  <SettingCard $variant="warning" $active={settings.stopRequestIfUnpaid}>
                    <SettingContent>

                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>Block Move-Out with Pending Dues</SettingTitle>

                          <BadgeGroup>
                            {!settings.stopRequestIfUnpaid && (
                              <RecommendedBadge>Recommended</RecommendedBadge>
                            )}

                            {settings.stopRequestIfUnpaid && (
                              <StatusBadge $variant="warning">Active</StatusBadge>
                            )}
                          </BadgeGroup>
                        </TitleRow>

                        <SettingDescription>
                          Tenants must clear all dues before submitting a move-out request.
                        </SettingDescription>

                        <ImpactText className="text-green-600">
                          ↑ Prevents revenue loss from unpaid dues before tenant exit
                        </ImpactText>

                        {!settings.stopRequestIfUnpaid && (
                          <HelperText>
                            Tenants can request move-out even with unpaid dues.
                          </HelperText>
                        )}
                      </SettingHeader>

                      <ToggleWrapper>
                        <Toggle
                          checked={settings.stopRequestIfUnpaid}
                          onChange={() => toggleSetting("stopRequestIfUnpaid")}
                          disabled={isLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>

                    </SettingContent>
                  </SettingCard>

                  {/* Restrict timing */}
                  <SettingCard $variant="info" $active={settings.restrictMoveOutWindow}>
                    <SettingContent>

                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>Restrict Move-Out Timing</SettingTitle>

                          {settings.restrictMoveOutWindow && (
                            <StatusBadge $variant="info">Active</StatusBadge>
                          )}
                        </TitleRow>

                        <SettingDescription>
                          Move-out requests are allowed only on the rent cycle date.
                        </SettingDescription>

                        <ImpactText className="text-yellow-600">
                          → Helps align move-outs with billing cycle and reduces proration issues
                        </ImpactText>

                        {!settings.restrictMoveOutWindow && (
                          <HelperText>
                            Tenants can request move-out on any date.
                          </HelperText>
                        )}
                      </SettingHeader>

                      <ToggleWrapper>
                        <Toggle
                          checked={settings.restrictMoveOutWindow}
                          onChange={() => toggleSetting("restrictMoveOutWindow")}
                          disabled={isLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>

                    </SettingContent>
                  </SettingCard>

                  {/* Extra rent */}
                  {/* <SettingCard $variant="warning" $active={settings.extraMonthRentMidCycle}>
                    <SettingContent>

                      <SettingHeader>
                        <TitleRow>
                          <SettingTitle>Charge Full Month for Mid-Cycle Move-Out</SettingTitle>

                          {settings.extraMonthRentMidCycle && (
                            <StatusBadge $variant="warning">Active</StatusBadge>
                          )}
                        </TitleRow>

                        <SettingDescription>
                          Tenants must pay a full extra month’s rent if they leave mid-cycle.
                        </SettingDescription>

                        <ImpactText className="text-green-600">
                          ↑ Increases revenue and discourages mid-cycle move-outs
                        </ImpactText>

                        {!settings.extraMonthRentMidCycle && (
                          <HelperText>
                            Rent will be calculated proportionally for mid-cycle move-outs.
                          </HelperText>
                        )}
                      </SettingHeader>

                      <ToggleWrapper>
                        <Toggle
                          checked={settings.extraMonthRentMidCycle}
                          onChange={() => toggleSetting("extraMonthRentMidCycle")}
                          disabled={isLoading || isPropertyChanging}
                        />
                      </ToggleWrapper>

                    </SettingContent>
                  </SettingCard> */}
                </SettingsGrid>

                {/* Replicate Settings Section */}
                {filteredProperties.length > 1 && (
                  // <ReplicateSection>
                  //   <ReplicateHeader>
                  //     <ReplicateTitle>Replicate Settings to Other Properties</ReplicateTitle>
                  //   </ReplicateHeader>

                  //   <ReplicateContent>
                  //     <PropertiesSelection>
                  //       <CategoryCard
                  //         type="button"
                  //         selectedCategory={selectedProperties.length > 0 ? {
                  //           label: renderSelectedProperties(selectedProperties),
                  //           value: "properties"
                  //         } : null}
                  //         title="Property or Properties"
                  //         onClick={() => setShowMultiSelectModal(true)}
                  //       />

                  //       <NewPackage
                  //         onClick={handleReplicateSettings}
                  //         disabled={isLoading || selectedProperties.length === 0 || editSettingsLoading || isPropertyChanging}
                  //       >
                  //         {editSettingsLoading ? "Replicating..." : `Replicate to ${selectedProperties.length} Properties`}
                  //       </NewPackage>

                  //     </PropertiesSelection>
                  //   </ReplicateContent>
                  // </ReplicateSection>
                  <ReplicateSection>

                    {/* Header */}
                    <ReplicateHeader>
                      <ReplicateTitle>
                        Apply Settings to Other Properties
                      </ReplicateTitle>
                      <ReplicateSubtitle>
                        Instantly copy these settings across selected properties
                      </ReplicateSubtitle>
                    </ReplicateHeader>

                    <ReplicateContent>

                      {/* Selection Card */}
                      <ReplicateSelectionCard
                        type="button"
                        onClick={() => setShowMultiSelectModal(true)}
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Select Properties
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {selectedProperties.length > 0
                              ? renderSelectedProperties(selectedProperties)
                              : "Choose one or more properties"}
                          </p>
                        </div>

                        <span className="text-primary-color text-sm font-medium">
                          {selectedProperties.length > 0 ? "Edit" : "Select"}
                        </span>
                      </ReplicateSelectionCard>

                      {/* CTA Button */}
                      <PrimaryAction
                        onClick={handleReplicateSettings}
                        disabled={isLoading || selectedProperties.length === 0 || editSettingsLoading}
                      >
                        {editSettingsLoading
                          ? "Applying..."
                          : selectedProperties.length > 0
                            ? `Apply to ${selectedProperties.length} Properties`
                            : "Select Properties First"}
                      </PrimaryAction>

                    </ReplicateContent>
                  </ReplicateSection>
                )}
              </Section>

            </ContentWrapper>

            {/* Single Property Selection Modal */}
            <SinglePropSelectModal
              isOpen={showPropertyModal}
              onClose={() => setShowPropertyModal(false)}
              onSelect={handleSelectedProperty}
              options={filteredProperties}
              selectedProperty={selectedProperty}
            />

            {/* Multi Property Selection Modal */}
            <MultiPropSelectModal
              isOpen={showMultiSelectModal}
              onClose={() => setShowMultiSelectModal(false)}
              onSelect={handleMultiPropertySave}
              options={filteredProperties}
              selectedProperties={selectedProperties}
            />
          </ContentContainer>
        </>
      )}
    </div>
  );
};

const ContentContainer = tw.div`flex-1 overflow-y-auto`;
const ContentWrapper = tw.div`p-6`;
const Section = tw.div`space-y-6`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] p-4`;
const HeaderTitle = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button``;
const PropertySection = tw.div`bg-gray-50 rounded-xl p-4 border border-gray-200 mb-4`;
const PropertyDisplayContainer = tw.div`flex items-center gap-1 truncate`;
const RemainingCount = tw.span`text-gray-500 ml-1 font-medium`;
const SettingsGrid = tw.div`space-y-4`;
// const SettingCard = tw.div`bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-200`;
// const SettingContent = tw.div`flex items-start justify-between space-x-6 p-5`;
// const SettingHeader = tw.div`flex-1`;
// const SettingTitle = tw.h3`font-semibold text-gray-800 mb-2 text-base`;
// const SettingDescription = tw.p`text-sm text-gray-600 leading-relaxed`;

const Toggle = ({ checked, onChange, disabled }) => (
  <ToggleLabel disabled={disabled}>
    <ToggleInput
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <ToggleSlider checked={checked} disabled={disabled}>
      <ToggleThumb checked={checked} />
    </ToggleSlider>
  </ToggleLabel>
);

const ToggleLabel = tw.label`${props => props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} relative inline-flex items-center`;
const ToggleInput = tw.input`sr-only peer`;
// const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.disabled ? 'bg-gray-200 cursor-not-allowed' : props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
const ToggleThumb = tw.div`absolute top-0.5 left-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${props => props.checked ? 'transform translate-x-6' : ''}`;
const PropertiesSelection = tw.div`space-y-3`;
const NewPackage = tw.button`w-full border border-primary-color items-center mb-2 text-primary-color h-12 rounded-lg hover:bg-primary-color hover:text-white`;


const SettingCard = tw.div`
  relative rounded-xl border p-4 transition-all duration-200 bg-white
  ${(p) =>
    p.$active
      ? "border-gray-300 shadow-sm"
      : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}
`;

const SettingContent = tw.div`
  flex items-center justify-between gap-4
`;

const SettingHeader = tw.div`flex-1`;

const SettingTitle = tw.h3`
  text-sm font-semibold text-gray-800
`;

const SettingDescription = tw.p`
  text-xs text-gray-500 leading-relaxed
`;

const ToggleWrapper = tw.div`
  flex items-center justify-center
`;

const StatusBadge = tw.span`
  text-[10px] font-medium px-2 py-0.5 rounded-full
  ${(p) =>
    p.$variant === "warning"
      ? "bg-yellow-100 text-yellow-700"
      : "bg-green-100 text-green-700"}
`;

const HelperText = tw.p`
  text-xs text-gray-400 mt-1
`;

const TitleRow = tw.div`
  flex items-center justify-between gap-2 mb-1
`;


export default EvictionSettingsModal;
